import { readUser, checkRoot, isKaizenOnlyUser } from '@app/services/localStorage.service';
import permissionsData from './kaizen-permissions.json';

interface KaizenPermissionConfig {
  kaizenLeads: Record<string, string>;
  admins?: {
    emails?: string[];
    usernames?: string[];
  };
}

const permissions = permissionsData as KaizenPermissionConfig;

/** Email đăng nhập: BE/localStorage có thể là string hoặc `{ name }`. */
const normalizeEmailValue = (raw: unknown): string | null => {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const s = raw.trim();
    return s || null;
  }
  if (typeof raw === 'object' && raw !== null) {
    const name = String((raw as { name?: string }).name ?? '').trim();
    return name || null;
  }
  return null;
};

/**
 * Get current user's email
 */
const getCurrentUserEmail = (): string | null => {
  const user = readUser();
  return normalizeEmailValue(user?.email);
};

const getCurrentUserName = (): string | null => {
  const user = readUser();
  return user?.userName || null;
};

/**
 * Check if user is Kaizen Lead for a specific area
 * @param areaName - Name of the area (e.g., "INT", "POLE", "SPACER")
 * @param email - Optional email to check. If not provided, uses current logged-in user's email
 * @returns true if user is Kaizen Lead for the area
 */
export const checkIsKaizenLeadForArea = (areaName: string, email?: string): boolean => {
  const userEmail = email || getCurrentUserEmail();
  if (!userEmail || !areaName) {
    return false;
  }

  const emailLower = userEmail.toLowerCase().trim();
  const normalizedAreaName = areaName.trim().toUpperCase();
  const areaLeadEmail = Object.entries(permissions.kaizenLeads).find(
    ([key]) => key.trim().toUpperCase() === normalizedAreaName,
  )?.[1];

  if (!areaLeadEmail) {
    return false;
  }

  return areaLeadEmail.toLowerCase() === emailLower;
};

const checkIsAnyKaizenLead = (email?: string): boolean => {
  const userEmail = email || getCurrentUserEmail();
  if (!userEmail) {
    return false;
  }
  const emailLower = userEmail.toLowerCase().trim();
  return Object.values(permissions.kaizenLeads).some((leadEmail) => leadEmail.toLowerCase().trim() === emailLower);
};

const checkIsConfiguredAdmin = (): boolean => {
  const userEmail = (getCurrentUserEmail() || '').toLowerCase().trim();
  const userName = (getCurrentUserName() || '').toLowerCase().trim();
  const adminEmails = (permissions.admins?.emails || []).map((email) => email.toLowerCase().trim());
  const adminUsernames = (permissions.admins?.usernames || []).map((name) => name.toLowerCase().trim());
  return (!!userEmail && adminEmails.includes(userEmail)) || (!!userName && adminUsernames.includes(userName));
};

/** Admin đầy đủ quyền Kaizen (xóa, xuất, xác nhận, …) — {@link checkRoot} (user.isRoot) hoặc khai báo trong kaizen-permissions.json */
export const checkIsKaizenFullAdmin = (): boolean => {
  return checkIsConfiguredAdmin() || checkRoot();
};

export const checkIsAssignedHandler = (handlerId: string | null | undefined, currentUserId: string | null | undefined): boolean => {
  if (!handlerId || !currentUserId) {
    return false;
  }
  return String(handlerId) === String(currentUserId);
};

/** Tạo mới — mọi vai (kể cả tài khoản Kaizen-only JWT). */
export const canCreateIdea = (): boolean => {
  return true;
};

/** Sửa thông tin ý tưởng — mọi vai. */
export const canEditIdea = (_areaName?: string | null): boolean => {
  void _areaName;
  return true;
};

/**
 * Phân công — không cho tài khoản đặc biệt (JWT Kaizen-only); các vai khác được phép.
 */
export const canAssignIdea = (_areaName?: string | null): boolean => {
  void _areaName;
  return !isKaizenOnlyUser();
};

/**
 * Xử lý — không cho tài khoản đặc biệt; Admin / Kaizen lead khu vực / người được phân công (handler).
 */
export const canHandleIdea = (
  handlerId?: string | null,
  currentUserId?: string | null,
  areaName?: string | null,
): boolean => {
  if (isKaizenOnlyUser()) return false;
  if (checkIsKaizenFullAdmin()) return true;
  const area = String(areaName || '').trim();
  if (area) {
    if (checkIsKaizenLeadForArea(area)) return true;
  } else if (checkIsAnyKaizenLead()) {
    return true;
  }
  return checkIsAssignedHandler(handlerId, currentUserId);
};

export type CanApproveIdeaOptions = {
  /** Email Kaizen lead gắn trên ý tưởng (BE: `kaizen_lead_email`) — ưu tiên khi area chưa có trong JSON. */
  kaizenLeadEmail?: string | null;
};

/**
 * Xác nhận — Admin, Kaizen lead khu vực (kaizen-permissions.json), hoặc đúng lead email trên idea.
 */
export const canApproveIdea = (areaName?: string | null, options?: CanApproveIdeaOptions | null): boolean => {
  if (checkIsKaizenFullAdmin()) {
    return true;
  }

  const ideaLeadEmail = normalizeEmailValue(options?.kaizenLeadEmail);
  const userEmail = getCurrentUserEmail();
  if (ideaLeadEmail && userEmail && ideaLeadEmail.toLowerCase() === userEmail.toLowerCase()) {
    return true;
  }

  const area = String(areaName || '').trim();
  if (!area) {
    return checkIsAnyKaizenLead();
  }
  return checkIsKaizenLeadForArea(area);
};

/**
 * Xuất báo cáo — Admin hoặc Kaizen lead (theo area nếu truyền; không truyền thì lead bất kỳ khu vực).
 */
export const canExportReport = (areaName?: string | null): boolean => {
  if (checkIsKaizenFullAdmin()) return true;
  const area = String(areaName || '').trim();
  if (area) {
    return checkIsKaizenLeadForArea(area);
  }
  return checkIsAnyKaizenLead();
};

/** Xóa — chỉ Admin ({@link checkRoot} hoặc admin trong JSON). */
export const canDeleteIdea = (): boolean => {
  return checkIsKaizenFullAdmin();
};

export const canViewMyTasks = (): boolean => {
  return true;
};
